import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BILLING_TABS, normalizeBillingTab } from '../../lib/billingTabs.js';
import { getEvidenceVaultTiers } from '../../lib/evidenceVault.js';
import {
  RECENT_PLATFORM_CHARGES_DAYS,
  chargePaymentStatus,
  filterRecentPlatformCharges,
  isChargePaid,
} from '../../lib/recentPlatformCharges.js';
import {
  PLATFORM_INVOICE_RETENTION_MONTHS,
  invoiceKind,
  invoiceListTitle,
  invoiceNumber,
  invoicePaymentStatus,
  invoiceReceipts,
  invoiceTotalAmount,
  paymentStatusClass,
  paymentStatusLabel,
  retainPlatformInvoices,
} from '../../lib/platformInvoices.js';
import {
  INTEGRATION_PRICING,
  SEAT_ADDON_PRICING,
  demandFeeVolumeLabel,
  formatDemandFeePrice,
  formatIntegrationPrice,
  formatSeatAddonPrice,
  getDemandFeeEntitlement,
  getDemandFeeTiers,
  integrationPricingForConnections,
  seatAddonPricingForUsers,
} from '../../lib/settingsStore.js';

export { BILLING_TABS, normalizeBillingTab };

/** Copy for the Evidence Locker now/new confirmation. Price up from $0 says the charge is added; a paid upgrade states the new monthly bill; a smaller plan warns that uploads stop. */
export function vaultPlanConfirmCopy(current, next) {
  const currentPrice = Number(current?.monthlyPrice) || 0;
  const nextPrice = Number(next?.monthlyPrice) || 0;
  if (nextPrice > currentPrice && currentPrice === 0) {
    return {
      title: `Upgrade to ${next.label}`,
      action: 'Upgrade',
      note: `This replaces your ${current.label} plan. The $${nextPrice} is added to this month’s bill.`,
    };
  }
  if (nextPrice > currentPrice) {
    return {
      title: `Upgrade to ${next.label}`,
      action: 'Upgrade',
      note: `This replaces your ${current.label} plan. You’ll be billed $${nextPrice} per month.`,
    };
  }
  const billed = nextPrice > 0 ? ` You’ll be billed $${nextPrice} per month.` : '';
  return {
    title: `Change to ${next.label}`,
    action: 'Change plan',
    note: `This replaces your ${current.label} plan. New uploads stop if you are already over ${next.gbLimit} GB.${billed}`,
  };
}

function cardBrandMark(brand) {
  const key = String(brand || '').trim().toLowerCase();
  if (key.includes('master')) return { label: 'MC', tone: 'mc' };
  if (key.includes('amex') || key.includes('american')) return { label: 'AMEX', tone: 'amex' };
  return { label: 'VISA', tone: 'visa' };
}

function formatChargeDate(iso, { withYear = false } = {}) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
    });
  } catch {
    return '—';
  }
}

function formatMoneyAmount(amount, currency = 'USD') {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'USD'),
      minimumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  }
}

/** Option A strip: primary title + quiet subtitle (no fee/card clutter). */
function receiptStripParts(rcpt) {
  const status = String(rcpt?.status || '').toUpperCase();
  const kind = String(rcpt?.kind || '').toLowerCase();
  if (kind === 'platform') {
    return {
      title: String(rcpt?.label || '').trim() || 'Platform subscription',
      subtitle:
        status === 'UNPAID' ? 'Payment declined' : status === 'OPEN' ? 'Charge pending' : null,
    };
  }
  const demandId = String(rcpt?.demandId || '').trim();
  const parts = String(rcpt?.label || '')
    .split(' · ')
    .map((part) => part.trim())
    .filter(Boolean);
  if (demandId) {
    return {
      title: demandId,
      subtitle: parts.length > 1 ? parts.slice(1).join(' · ') : null,
    };
  }
  return {
    title: parts[0] || 'Demand fee',
    subtitle: parts.length > 1 ? parts.slice(1).join(' · ') : null,
  };
}

export default function BillingPanel({
  billing,
  onChange,
  onAddPaymentMethod,
  onSetDefaultPaymentMethod,
  onRemovePaymentMethod,
  onSelectVaultPlan,
  vaultUsage,
  connections = [],
  orgUsers = [],
  pending,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = normalizeBillingTab(searchParams.get('billingTab'));
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  /** Mobile Option B: list first; open detail only after tapping a month. */
  const [invoiceDrillOpen, setInvoiceDrillOpen] = useState(false);
  /** Per-tab Option B: which pricing-terms accordion section is open. */
  const [feeTermsOpen, setFeeTermsOpen] = useState('how-tiers');
  const [integrationsTermsOpen, setIntegrationsTermsOpen] = useState('rate');
  const [lockerTermsOpen, setLockerTermsOpen] = useState('manage');
  /** Evidence Locker plan the user picked but has not confirmed. */
  const [pendingVaultPlanId, setPendingVaultPlanId] = useState(null);
  const vaultConfirmRef = useRef(null);

  const vaultTiers = getEvidenceVaultTiers();
  const vaultPlanId = vaultUsage?.planId || billing.evidenceVaultPlanId || 'included';
  const vaultPaid = vaultPlanId !== 'included';
  const vaultTierMeta = vaultTiers.find((t) => t.id === vaultPlanId) || vaultTiers[0];
  const pendingVaultTier =
    vaultTiers.find((tier) => tier.id === pendingVaultPlanId && tier.id !== vaultPlanId) || null;
  const vaultConfirm = pendingVaultTier
    ? vaultPlanConfirmCopy(vaultTierMeta, pendingVaultTier)
    : null;

  useEffect(() => {
    if (activeTab !== 'evidence-locker') setPendingVaultPlanId(null);
  }, [activeTab]);

  useEffect(() => {
    if (!pendingVaultPlanId) return undefined;
    vaultConfirmRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') setPendingVaultPlanId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingVaultPlanId]);

  const integrationPricing = integrationPricingForConnections(connections);
  const seatPricing = seatAddonPricingForUsers(orgUsers);
  const demandFee = getDemandFeeEntitlement(billing);
  const demandTiers = getDemandFeeTiers();
  const connectionUnit = formatIntegrationPrice(INTEGRATION_PRICING.connectionMonthly);
  const activeConnectionNames = connections
    .filter((conn) => conn.status === 'connected')
    .map((conn) => conn.name)
    .filter(Boolean);
  const integrationChargeDetail =
    integrationPricing.connectedCount === 0
      ? `No active connections. Each app you connect adds ${connectionUnit} per month.`
      : `${
          activeConnectionNames.length
            ? activeConnectionNames.join(', ')
            : `${integrationPricing.connectedCount} active connection${
                integrationPricing.connectedCount === 1 ? '' : 's'
              }`
        } · ${connectionUnit} per connection`;
  const seatUnit = formatSeatAddonPrice(SEAT_ADDON_PRICING.extraSeatMonthly);
  const extraSeatNames = orgUsers
    .filter((user) => user.role !== 'OWNER')
    .map((user) => user.name || user.email)
    .filter(Boolean);
  const seatChargeDetail =
    seatPricing.extraSeats === 0
      ? `Owner seat included. Each teammate you add costs ${seatUnit} per month.`
      : `${
          extraSeatNames.length === seatPricing.extraSeats
            ? extraSeatNames.join(', ')
            : `${seatPricing.extraSeats} extra seat${seatPricing.extraSeats === 1 ? '' : 's'}`
        } · ${seatUnit} per seat`;
  const recentCharges = filterRecentPlatformCharges(billing.payments);
  const retainedInvoices = retainPlatformInvoices(billing.invoices);
  const retainedInvoiceIds = retainedInvoices.map((inv) => inv.id).join('|');
  const selectedInvoice =
    retainedInvoices.find((inv) => inv.id === selectedInvoiceId) || retainedInvoices[0] || null;

  useEffect(() => {
    if (!retainedInvoiceIds) {
      setSelectedInvoiceId(null);
      setInvoiceDrillOpen(false);
      return;
    }
    const ids = retainedInvoiceIds.split('|');
    if (!selectedInvoiceId || !ids.includes(selectedInvoiceId)) {
      setSelectedInvoiceId(ids[0]);
      setInvoiceDrillOpen(false);
    }
  }, [retainedInvoiceIds, selectedInvoiceId]);

  useEffect(() => {
    if (activeTab !== 'invoices') setInvoiceDrillOpen(false);
  }, [activeTab]);

  function setBillingTab(nextId) {
    const next = normalizeBillingTab(nextId);
    const params = new URLSearchParams(searchParams);
    params.set('billingTab', next);
    setSearchParams(params, { replace: true });
  }

  function openInvoiceDetail(invoiceId) {
    setSelectedInvoiceId(invoiceId);
    setInvoiceDrillOpen(true);
  }

  return (
    <div className="settings-form billing-settings">
      <h2>Billing</h2>

      <div className="record-tabs-wrap">
        <div className="record-tabs" role="tablist" aria-label="Billing sections">
          {BILLING_TABS.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`billing-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`billing-panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                className={selected ? 'record-tab active' : 'record-tab'}
                onClick={() => setBillingTab(tab.id)}
              >
                <span className="record-tab-label">
                  <span className="record-tab-label-full">{tab.label}</span>
                  <span className="record-tab-label-short">{tab.shortLabel}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="record-tab-panels">
        {activeTab === 'payment-method' ? (
          <section
            id="billing-panel-payment-method"
            role="tabpanel"
            aria-labelledby="billing-tab-payment-method"
            className="billing-tab-panel billing-payment-method"
          >
            <div className="billing-section-toolbar">
              <div>
                <h3>Payment methods</h3>
                <p className="billing-section-meta">Used for DemandJet platform billing</p>
              </div>
              <button
                type="button"
                className="btn-primary"
                disabled={pending}
                onClick={onAddPaymentMethod}
              >
                + Add payment method
              </button>
            </div>

            {billing.paymentMethods?.length ? (
              <ul className="billing-card-list">
                {billing.paymentMethods.map((pm) => {
                  const mark = cardBrandMark(pm.brand);
                  return (
                    <li
                      key={pm.id}
                      className={
                        pm.isDefault ? 'billing-card-row is-default' : 'billing-card-row'
                      }
                    >
                      <span
                        className={`billing-card-mark billing-card-mark--${mark.tone}`}
                        aria-hidden="true"
                      >
                        {mark.label}
                      </span>
                      <div className="billing-card-main">
                        <strong>
                          ···· {pm.last4}
                          {pm.isDefault ? <span className="demo-pill">Default</span> : null}
                        </strong>
                        <span className="muted-line">
                          Expires {pm.expMonth}/{pm.expYear}
                        </span>
                      </div>
                      <div className="billing-card-actions">
                        {pm.isDefault ? (
                          <button
                            type="button"
                            className="btn-ghost"
                            disabled={pending}
                            onClick={() => onRemovePaymentMethod?.(pm.id)}
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-ghost"
                            disabled={pending}
                            onClick={() => onSetDefaultPaymentMethod?.(pm.id)}
                          >
                            Make default
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="settings-sublead">No payment methods on file yet.</p>
            )}

            <div className="billing-quiet-charges">
              <div className="billing-section-toolbar billing-section-toolbar--quiet">
                <div>
                  <h3>Recent platform charges</h3>
                  <p className="billing-section-meta">
                    Last {RECENT_PLATFORM_CHARGES_DAYS} days · older charges are on Invoices
                  </p>
                </div>
                <button
                  type="button"
                  className="text-link strong billing-all-invoices"
                  onClick={() => setBillingTab('invoices')}
                >
                  All invoices
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path
                      d="M9 5.5 15.5 12 9 18.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              {recentCharges.length ? (
                <ul className="billing-charge-list">
                  {recentCharges.map((pay) => {
                    const status = chargePaymentStatus(pay);
                    const paid = isChargePaid(pay);
                    return (
                      <li key={pay.id} className="billing-charge-row">
                        <span className="billing-charge-date">
                          {formatChargeDate(pay.paidAt || pay.attemptedAt)}
                        </span>
                        <span className="billing-charge-label">{pay.label}</span>
                        <span
                          className={`billing-invoice-status ${paymentStatusClass(status)}`}
                        >
                          {paymentStatusLabel(status)}
                        </span>
                        <strong className="billing-charge-amount">
                          {formatMoneyAmount(pay.amount, pay.currency)}
                        </strong>
                        {paid && pay.receiptUrl ? (
                          <a className="text-link strong" href={pay.receiptUrl}>
                            Receipt
                          </a>
                        ) : (
                          <span className="billing-charge-spacer" aria-hidden="true">
                            —
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="settings-sublead">
                  No charges in the last {RECENT_PLATFORM_CHARGES_DAYS} days.{' '}
                  <button
                    type="button"
                    className="text-link strong billing-inline-link"
                    onClick={() => setBillingTab('invoices')}
                  >
                    View invoices
                  </button>
                </p>
              )}
            </div>
          </section>
        ) : null}

        {activeTab === 'per-demand-fee' ? (
          <section
            id="billing-panel-per-demand-fee"
            role="tabpanel"
            aria-labelledby="billing-tab-per-demand-fee"
            className="billing-tab-panel billing-plan-panel"
          >
            <div className="billing-plan-head">
              <div>
                <p className="billing-plan-eyebrow">Billing</p>
                <h3>Per-demand fee</h3>
                <p className="billing-plan-lead">
                  Higher monthly volume means a lower price per demand. We average the
                  last 3 months to set next month’s rate.
                </p>
              </div>
              <span className="billing-plan-current-pill">
                Current · {demandFee.tierLabel}
              </span>
            </div>

            <div className="billing-plan-usage-box">
              <strong>{demandFee.usage?.headline}</strong>
              <span>{demandFee.usage?.hint}</span>
              <div
                className="billing-plan-usage-meter"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={demandFee.usage?.meterPct ?? 0}
                aria-label="Progress toward next fee tier"
              >
                <i style={{ width: `${demandFee.usage?.meterPct ?? 0}%` }} />
              </div>
            </div>

            <ul className="billing-plan-card-grid is-dense">
              {demandTiers.map((tier) => {
                const selected = tier.id === demandFee.tierId;
                return (
                  <li key={tier.id}>
                    <div
                      className={
                        selected
                          ? 'billing-plan-card is-static is-selected'
                          : 'billing-plan-card is-static'
                      }
                    >
                      {selected ? (
                        <span className="billing-plan-card-tag">Current</span>
                      ) : null}
                      <span className="billing-plan-card-title">
                        <strong>{tier.label}</strong>
                        <span className="muted-line">{demandFeeVolumeLabel(tier)}</span>
                      </span>
                      <span className="billing-plan-card-price">
                        {formatDemandFeePrice(tier.perDemandPrice)}
                        <small>/ demand</small>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>

            <BillingTermsAccordion
              label="Per-demand pricing terms"
              openId={feeTermsOpen}
              onToggle={setFeeTermsOpen}
              sections={[
                {
                  id: 'how-tiers',
                  title: 'How tiers work',
                  shortTitle: 'How tiers work',
                  body: null,
                  bullets: [
                    'The more demands attorneys accept for you each month, the less each one costs.',
                    'Your tier updates automatically based on your last 3 months’ average, so one slow or busy month won’t swing your price.',
                    'You’ll get a heads-up before your tier changes.',
                  ],
                },
                {
                  id: 'when-charged',
                  title: 'When you are charged',
                  shortTitle: 'When charged',
                  body: null,
                  bullets: [
                    'You only pay when an attorney accepts your demand. Creating and sending demands is free.',
                    'When you submit a demand, we place a hold on your card — no charge yet. The charge happens once an attorney accepts.',
                    'We never take a share of what you recover.',
                  ],
                },
                {
                  id: 'early-pay',
                  title: 'Paid before assignment',
                  shortTitle: 'Early pay',
                  body: null,
                  bullets: [
                    'If your customer pays in full before an attorney accepts your demand, the demand closes and you aren’t charged.',
                    'If your customer pays after an attorney has already accepted, you’re still charged and the fee is not refunded.',
                  ],
                },
                {
                  id: 'invoices',
                  title: 'How it appears on invoices',
                  shortTitle: 'On invoices',
                  body: null,
                  bullets: [
                    'Everything is combined into one invoice each month.',
                    'Per-demand fees are charged each time an attorney accepts a demand you’ve submitted. Each one is listed on the invoice with its own receipt.',
                    'Add-ons (apps, seats, and Evidence Locker) are charged together once a month as one subscription, and itemized on the invoice.',
                  ],
                },
              ]}
            />
          </section>
        ) : null}

        {activeTab === 'integrations' ? (
          <section
            id="billing-panel-integrations"
            role="tabpanel"
            aria-labelledby="billing-tab-integrations"
            className="billing-tab-panel billing-plan-panel"
          >
            <div className="billing-plan-head">
              <div>
                <p className="billing-plan-eyebrow">Billing</p>
                <h3>Integrations add-on</h3>
                <p className="billing-plan-lead">
                  What you pay this month for the accounting apps you have connected.
                </p>
              </div>
              <span className="billing-plan-current-pill">
                Current · {integrationPricing.connectedCount} connection
                {integrationPricing.connectedCount === 1 ? '' : 's'}
              </span>
            </div>

            <div className="billing-addon-charge" aria-label="Integrations charge this month">
              <div className="billing-addon-charge-copy">
                <strong>This month</strong>
                <span>{integrationChargeDetail}</span>
                <Link className="text-link billing-addon-charge-link" to="?tab=integrations">
                  Add an integration
                </Link>
              </div>
              <p className="billing-addon-charge-price">
                {formatIntegrationPrice(integrationPricing.monthlyTotal)}
                <small>/ mo</small>
              </p>
            </div>

            <BillingTermsAccordion
              label="Integrations pricing terms"
              openId={integrationsTermsOpen}
              onToggle={setIntegrationsTermsOpen}
              sections={[
                {
                  id: 'rate',
                  title: 'Rate',
                  shortTitle: 'Rate',
                  body: null,
                  bullets: [
                    `Flat rate: ${connectionUnit} per connection per month.`,
                    'Applies to connectors such as QuickBooks, Xero, Zoho Books, and other available accounting apps.',
                  ],
                },
                {
                  id: 'billing',
                  title: 'Billing cycle',
                  shortTitle: 'Billing',
                  body: null,
                  bullets: [
                    'Billed monthly on top of per-demand fees and other add-ons.',
                    'Disconnecting a connector stops that connection’s monthly add-on at the next billing cycle (demo accounts update immediately).',
                  ],
                },
                {
                  id: 'manage',
                  title: 'Manage connections',
                  shortTitle: 'Manage',
                  body: null,
                  bullets: [
                    <>
                      Connect or disconnect apps from{' '}
                      <Link className="text-link" to="?tab=integrations">
                        Settings → Integrations
                      </Link>
                      .
                    </>,
                    'Only active connections count toward this month’s total.',
                  ],
                },
              ]}
            />
          </section>
        ) : null}

        {activeTab === 'seats' ? (
          <section
            id="billing-panel-seats"
            role="tabpanel"
            aria-labelledby="billing-tab-seats"
            className="billing-tab-panel billing-plan-panel"
          >
            <div className="billing-plan-head">
              <div>
                <p className="billing-plan-eyebrow">Billing</p>
                <h3>Extra user seats</h3>
                <p className="billing-plan-lead">
                  What you pay this month for teammates beyond the included owner seat.
                </p>
              </div>
              <span className="billing-plan-current-pill">
                Current · {seatPricing.extraSeats} extra
              </span>
            </div>

            <div className="billing-addon-charge" aria-label="Seat charge this month">
              <div className="billing-addon-charge-copy">
                <strong>This month</strong>
                <span>{seatChargeDetail}</span>
                <Link className="text-link billing-addon-charge-link" to="?tab=users">
                  Add a teammate
                </Link>
              </div>
              <p className="billing-addon-charge-price">
                {formatSeatAddonPrice(seatPricing.monthlyTotal)}
                <small>/ mo</small>
              </p>
            </div>

            <BillingTermsAccordion
              label="Seat pricing terms"
              lockedOpen
              sections={[
                {
                  id: 'manage',
                  title: 'Manage users',
                  shortTitle: 'Manage',
                  body: null,
                  bullets: [
                    <>
                      Invite or remove teammates from{' '}
                      <Link className="text-link" to="?tab=users">
                        Settings → Users & roles
                      </Link>
                      .
                    </>,
                    'Seat totals update with your org roster for the next billing cycle (demo accounts update immediately).',
                  ],
                },
              ]}
            />
          </section>
        ) : null}

        {activeTab === 'evidence-locker' ? (
          <section
            id="billing-panel-evidence-locker"
            role="tabpanel"
            aria-labelledby="billing-tab-evidence-locker"
            className="billing-tab-panel billing-plan-panel"
          >
            <div className="billing-plan-head">
              <div>
                <p className="billing-plan-eyebrow">Billing</p>
                <h3>Evidence Locker</h3>
                <p className="billing-plan-lead">
                  Cloud storage for invoices, evidence files, and demands documents.
                </p>
              </div>
              <span className="billing-plan-current-pill">
                Current · {vaultUsage?.tierLabel || vaultTierMeta?.label || 'Included'}
              </span>
            </div>

            <ul className="billing-plan-card-grid is-dense">
              {vaultTiers.map((tier) => {
                const selected = tier.id === vaultPlanId;
                const priced = tier.monthlyPrice > 0;
                return (
                  <li key={tier.id}>
                    <button
                      type="button"
                      className={
                        selected ? 'billing-plan-card is-selected' : 'billing-plan-card'
                      }
                      disabled={pending || selected}
                      onClick={() => setPendingVaultPlanId(tier.id)}
                      aria-pressed={selected}
                    >
                      {selected ? (
                        <span className="billing-plan-card-tag">Current</span>
                      ) : null}
                      <span className="billing-plan-card-title">
                        <strong>{tier.label}</strong>
                        <span className="muted-line">
                          {tier.gbLimit} GB
                          {priced ? '' : ' · with every account'}
                        </span>
                      </span>
                      <span className="billing-plan-card-price">
                        {priced ? (
                          <>
                            ${tier.monthlyPrice}
                            <small>/ mo</small>
                          </>
                        ) : (
                          <>
                            $0<small>/ mo</small>
                          </>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <BillingTermsAccordion
              label="Evidence Locker pricing terms"
              openId={lockerTermsOpen}
              onToggle={setLockerTermsOpen}
              sections={[
                {
                  id: 'manage',
                  title: 'Manage storage',
                  shortTitle: 'Manage',
                  body: 'Your plan decides how much you can store. Invoices, evidence files, and demand documents all use that space.',
                  bullets: [
                    'The demands you create don’t use up storage.',
                    'When your Locker is full, you can’t add more files until you delete some or pick a bigger plan.',
                    'You can delete files anytime in Evidence Locker to free up space.',
                  ],
                },
              ]}
            />

            <p className="billing-plan-usage" style={{ marginTop: '0.15rem' }}>
              <Link className="text-link strong" to="/client/evidence-locker">
                Open Evidence Locker
              </Link>
              {' · '}
              <Link className="text-link strong" to="/client/demands/archived">
                View archived demands
              </Link>
            </p>

            {pendingVaultTier && vaultConfirm ? (
              <div
                className="social-modal-backdrop"
                role="presentation"
                onClick={() => setPendingVaultPlanId(null)}
              >
                <div
                  className="social-modal billing-plan-confirm"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="billing-plan-confirm-title"
                  aria-describedby="billing-plan-confirm-note"
                  onClick={(event) => event.stopPropagation()}
                >
                  <h2 id="billing-plan-confirm-title">{vaultConfirm.title}</h2>
                  <div className="billing-plan-confirm-swap">
                    <div className="billing-plan-confirm-plan">
                      <span>Now</span>
                      <strong>{vaultTierMeta.label}</strong>
                      <em>
                        {vaultTierMeta.gbLimit} GB · ${vaultTierMeta.monthlyPrice}/mo
                      </em>
                    </div>
                    <div className="billing-plan-confirm-arrow" aria-hidden="true">
                      →
                    </div>
                    <div className="billing-plan-confirm-plan is-next">
                      <span>New</span>
                      <strong>{pendingVaultTier.label}</strong>
                      <em>
                        {pendingVaultTier.gbLimit} GB · ${pendingVaultTier.monthlyPrice}/mo
                      </em>
                    </div>
                  </div>
                  <p id="billing-plan-confirm-note">{vaultConfirm.note}</p>
                  <div className="billing-plan-confirm-actions">
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => setPendingVaultPlanId(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      ref={vaultConfirmRef}
                      disabled={pending}
                      onClick={() => {
                        onSelectVaultPlan?.(pendingVaultTier.id);
                        setPendingVaultPlanId(null);
                      }}
                    >
                      {vaultConfirm.action}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {activeTab === 'invoices' ? (
          <section
            id="billing-panel-invoices"
            role="tabpanel"
            aria-labelledby="billing-tab-invoices"
            className="billing-tab-panel billing-invoices"
          >
            <div className="billing-section-toolbar">
              <div>
                <h3>Invoices &amp; receipts</h3>
                <p className="billing-section-meta billing-section-meta--desktop">
                  Select an invoice to download the bill and its payment receipt · last{' '}
                  {PLATFORM_INVOICE_RETENTION_MONTHS} months
                </p>
                <p className="billing-section-meta billing-section-meta--mobile">
                  Tap a month for receipts · last {PLATFORM_INVOICE_RETENTION_MONTHS} months
                </p>
              </div>
            </div>

            {retainedInvoices.length && selectedInvoice ? (
              <div
                className={[
                  'billing-invoice-layout',
                  invoiceDrillOpen ? 'is-drill-open' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <ul className="billing-invoice-stack" aria-label="Invoices">
                  {retainedInvoices.map((inv) => {
                    const invStatus = invoicePaymentStatus(inv);
                    const paid = invStatus === 'PAID';
                    const kind = invoiceKind(inv);
                    const isMonthly = kind === 'monthly';
                    const selected = inv.id === selectedInvoice.id;
                    const issued = formatChargeDate(inv.issuedAt, { withYear: true });
                    const pdfHref = inv.pdfUrl || inv.receiptUrl || '#';
                    return (
                      <li key={inv.id}>
                        <div
                          role="button"
                          tabIndex={0}
                          className={[
                            'billing-invoice-receipt billing-invoice-receipt--selectable',
                            invStatus === 'UNPAID'
                              ? 'is-unpaid'
                              : paid
                                ? ''
                                : 'is-open',
                            selected ? 'is-selected' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          aria-current={selected ? 'true' : undefined}
                          onClick={() => openInvoiceDetail(inv.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openInvoiceDetail(inv.id);
                            }
                          }}
                        >
                          <span className="billing-invoice-rail" aria-hidden="true" />
                          <div className="billing-invoice-period">
                            <strong>
                              {invoiceListTitle(inv)}
                              {kind === 'demand' ? (
                                <span className="billing-invoice-kind">Demand</span>
                              ) : null}
                            </strong>
                            <span className="muted-line">
                              {invoiceNumber(inv)}
                              {' · '}
                              Issued {issued}
                            </span>
                          </div>
                          <div className="billing-invoice-side">
                            {isMonthly ? null : (
                              <span
                                className={`billing-invoice-status ${paymentStatusClass(invStatus)}`}
                              >
                                {paymentStatusLabel(invStatus)}
                              </span>
                            )}
                            <strong className="billing-invoice-amount">
                              {formatMoneyAmount(invoiceTotalAmount(inv), inv.currency)}
                            </strong>
                            <a
                              className="text-link strong"
                              href={pdfHref}
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => event.stopPropagation()}
                            >
                              Invoice PDF
                            </a>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="billing-invoice-detail" aria-live="polite">
                  <div className="billing-invoice-detail-head">
                    <button
                      type="button"
                      className="billing-invoice-back"
                      onClick={() => setInvoiceDrillOpen(false)}
                      aria-label="Back to invoices"
                      title="Back to invoices"
                    >
                      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                        <path
                          d="M15.5 5.5 9 12l6.5 6.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <h4>
                      {invoiceListTitle(selectedInvoice)}
                      <span
                        className={`billing-invoice-status ${paymentStatusClass(
                          invoicePaymentStatus(selectedInvoice)
                        )}`}
                      >
                        {paymentStatusLabel(invoicePaymentStatus(selectedInvoice))}
                      </span>
                    </h4>
                  </div>

                  {invoiceReceipts(selectedInvoice).length ? (
                    <div className="billing-invoice-receipts">
                      <div className="billing-invoice-receipts-head">
                        <strong>Payment receipts</strong>
                        <span className="muted-line">
                          {invoiceReceipts(selectedInvoice).length} for this invoice
                        </span>
                      </div>
                      <ul className="billing-invoice-receipts-list">
                        {invoiceReceipts(selectedInvoice).map((rcpt) => {
                          const status = String(rcpt.status || '').toUpperCase();
                          const paid = status === 'PAID';
                          const { title, subtitle } = receiptStripParts(rcpt);
                          return (
                            <li
                              key={rcpt.id}
                              className={[
                                'billing-invoice-receipt-row',
                                status === 'UNPAID'
                                  ? 'is-unpaid'
                                  : paid
                                    ? 'is-paid'
                                    : 'is-open',
                              ].join(' ')}
                            >
                              <span className="billing-receipt-rail" aria-hidden="true" />
                              <div className="billing-invoice-period">
                                <strong className="billing-receipt-id">{title}</strong>
                                {subtitle ? (
                                  <span className="muted-line billing-receipt-debtor">
                                    {subtitle}
                                  </span>
                                ) : null}
                              </div>
                              <div className="billing-receipt-side">
                                <strong className="billing-invoice-amount">
                                  {formatMoneyAmount(rcpt.amount, rcpt.currency)}
                                </strong>
                                <span
                                  className={`billing-receipt-status ${paymentStatusClass(status)}`}
                                >
                                  {paymentStatusLabel(status)}
                                </span>
                              </div>
                              {paid && rcpt.pdfUrl ? (
                                <a className="text-link strong" href={rcpt.pdfUrl}>
                                  Receipt
                                </a>
                              ) : (
                                <span className="billing-invoice-step-pending">—</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="settings-sublead">
                No invoices in the last {PLATFORM_INVOICE_RETENTION_MONTHS} months.
              </p>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}

function BillingTermsSectionBody({ section }) {
  return (
    <div className="billing-plan-term-body">
      {section.body ? <p>{section.body}</p> : null}
      <ul>
        {section.bullets.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

function BillingTermsAccordion({ label, sections, openId, onToggle, lockedOpen = false }) {
  return (
    <div className="billing-plan-terms" role="region" aria-label={label}>
      {sections.map((section) => {
        const open = lockedOpen || openId === section.id;
        return (
          <div
            key={section.id}
            className={open ? 'billing-plan-term is-open' : 'billing-plan-term'}
          >
            {lockedOpen ? (
              <h4 className="billing-plan-term-static">{section.title}</h4>
            ) : (
              <button
                type="button"
                className="billing-plan-term-toggle"
                aria-expanded={open}
                onClick={() =>
                  onToggle((current) => (current === section.id ? null : section.id))
                }
              >
                <span className="billing-plan-term-title-full">{section.title}</span>
                <span className="billing-plan-term-title-short">{section.shortTitle}</span>
                <span className="billing-plan-term-chev" aria-hidden="true">
                  {open ? '▾' : '▸'}
                </span>
              </button>
            )}
            {open ? <BillingTermsSectionBody section={section} /> : null}
          </div>
        );
      })}
    </div>
  );
}
